<template>
    <div class="row">
        <div class="col-12 mb-2">
            <div class="border rounded-lg pb-1">
                <h5 class="mb-1 bg-light-primary px-1 py-75 border-bottom text-primary d-flex align-items-center">
                    <feather-icon icon="ListIcon" size="20" class="mr-50" />
                    Seçili Cezalar ({{ selectedFines.length }})
                </h5>
                <div class="table-responsive px-1">
                    <table class="table table-sm table-borderless mb-0">
                        <thead>
                            <tr>
                                <th>Tutanak No</th>
                                <th>Personel</th>
                                <th>Ceza Tarihi</th>
                                <th class="text-right">Ceza Tutarı</th>
                                <th class="text-right" title="Planlanan Personel Payı">Pln. Personel</th>
                                <th class="text-right" title="Planlanan Firma Payı">Pln. Firma</th>
                                <th title="Ödeme Tipi">Ödeme Tipi</th>
                                <th class="text-right" title="Faiz Tutarı">Faiz</th>
                                <th class="text-right" title="Gerçekleşecek Personel Payı">Grç. Personel</th>
                                <th class="text-right" title="Gerçekleşecek Firma Payı">Grç. Firma</th>
                                <th class="text-right" title="Ödenecek Toplam Tutar">Ödenecek Tutar</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="fine in selectedFines" :key="fine.EmployeeTrafficFineId">
                                <td>{{ fine.ReportNumber }}</td>
                                <td>{{ fine.EmployeeFullName }}</td>
                                <td>{{ fine.FineDateStr }}</td>
                                <td class="text-right">{{ fine.PriceStr }}</td>
                                <td class="text-right text-nowrap">{{ fine.PlannedDriverAmount ? formatCurrency(fine.PlannedDriverAmount) + ' ₺' : '-' }}</td>
                                <td class="text-right text-nowrap">{{ fine.PlannedCompanyAmount ? formatCurrency(fine.PlannedCompanyAmount) + ' ₺' : '-' }}</td>
                                <td>
                                    <span v-if="calculating" class="text-muted">...</span>
                                    <template v-else>
                                        <v-select-list
                                            v-if="getFineOptions(fine.EmployeeTrafficFineId).length > 0"
                                            :options="getFineOptions(fine.EmployeeTrafficFineId)"
                                            label="TypeName"
                                            :value="getOption(fine.EmployeeTrafficFineId)"
                                            :clearable="false"
                                            :disabled="getFineOptions(fine.EmployeeTrafficFineId).length === 1"
                                            append-to-body
                                            @input="onOptionChange(fine.EmployeeTrafficFineId, $event)"
                                            style="min-width: 160px;"
                                        ></v-select-list>
                                        <span v-else class="text-muted">-</span>
                                    </template>
                                </td>
                                <td class="text-right">
                                    <span v-if="calculating" class="text-muted">...</span>
                                    <template v-else>
                                        <span v-if="getOption(fine.EmployeeTrafficFineId) && getOption(fine.EmployeeTrafficFineId).InterestAmount > 0" class="text-danger">
                                            {{ formatCurrency(getOption(fine.EmployeeTrafficFineId).InterestAmount) }} ₺
                                        </span>
                                        <span v-else class="text-muted">-</span>
                                    </template>
                                </td>
                                <td class="text-right text-nowrap">
                                    <span v-if="calculating" class="text-muted">...</span>
                                    <template v-else>
                                        <span v-if="getActualEmployee(fine)" class="text-success font-weight-bold">
                                            {{ formatCurrency(getActualEmployee(fine)) }} ₺
                                        </span>
                                        <span v-else class="text-muted">-</span>
                                    </template>
                                </td>
                                <td class="text-right text-nowrap">
                                    <span v-if="calculating" class="text-muted">...</span>
                                    <template v-else>
                                        <span v-if="getActualCompany(fine)" class="text-success font-weight-bold">
                                            {{ formatCurrency(getActualCompany(fine)) }} ₺
                                        </span>
                                        <span v-else class="text-muted">-</span>
                                    </template>
                                </td>
                                <td class="text-right font-weight-bolder text-nowrap">
                                    <span v-if="calculating" class="text-muted">Hesaplanıyor...</span>
                                    <template v-else>
                                        <span v-if="getOption(fine.EmployeeTrafficFineId)" class="text-warning">
                                            {{ formatCurrency(getOption(fine.EmployeeTrafficFineId).PaymentAmount) }} ₺
                                        </span>
                                        <span v-else class="text-muted">-</span>
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="col-12 mb-1">
            <label class="form-label">Ödeme Tarihi <span class="text-danger">*</span></label>
            <v-date-picker placeholder="Ödeme Tarihi" v-model="value.PaymentDate" :disabled-date="disableFutureDates"></v-date-picker>
        </div>

        <div class="col-12 mb-1">
            <label class="form-label">Ödeme Yeri <span class="text-danger">*</span></label>
            <v-select-list append-to-body :options="paymentPlaces" label="Name" placeholder="Ödeme Yeri" v-model="paymentPlace"></v-select-list>
        </div>
        <div v-if="value.PaymentPlace == 0" class="col-12 mb-1">
            <label class="form-label">Vergi Dairesi <span class="text-danger">*</span></label>
            <v-select-list append-to-body v-model="taxAdministrator" :options="taxAdministrators" label="Name" placeholder="Vergi Dairesi"></v-select-list>
        </div>
        <div v-if="value.PaymentPlace == 1" class="col-12 mb-1">
            <label class="form-label">Banka <span class="text-danger">*</span></label>
            <v-select-list append-to-body v-model="bank" :options="banks" label="BankName" placeholder="Banka"></v-select-list>
        </div>
    </div>
</template>

<script>
import SystemDataApi from "@/api/system-data";
import BankApi from "@/api/bank";
import EmployeeOperationsApi from "@/api/employee-operations";

export default {
    name: "TrafficFineBulkPayment",
    props: {
        value: {
            type: Object,
            default: () => ({}),
        },
        selectedFines: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            paymentPlaces: [],
            taxAdministrators: [],
            banks: [],
            calculating: false,
            fineAmounts: {},
            selectedTypes: {},
            defaultTaxAdministratorId: null,
        };
    },
    mounted() {
        this.getPaymentPlaces();
        this.getBanks();
        this.getTaxAdministrators();
    },
    watch: {
        "value.PaymentDate": function(newDate) {
            this.fineAmounts = {};
            this.selectedTypes = {};
            this.$set(this.value, "Items", this.selectedFines.map(function(f) {
                return { EmployeeTrafficFineId: f.EmployeeTrafficFineId, PaidAmount: 0 };
            }));
            if (!newDate) return;
            if (this.defaultTaxAdministratorId && !this.value.TaxAdministrationId) {
                this.$set(this.value, "TaxAdministrationId", this.defaultTaxAdministratorId);
            }
            this.calculateAll(newDate);
        },
    },
    methods: {
        getFineOptions: function(fineId) {
            return this.fineAmounts[fineId] || [];
        },
        getSelectedType: function(fineId) {
            var opts = this.fineAmounts[fineId];
            if (!opts || opts.length === 0) return null;
            var t = this.selectedTypes[fineId];
            return t !== undefined ? t : opts[0].Type;
        },
        getOption: function(fineId) {
            var opts = this.fineAmounts[fineId];
            if (!opts || opts.length === 0) return null;
            var t = this.selectedTypes[fineId];
            if (t === undefined) return opts[0];
            return opts.find(function(o) { return o.Type === t; }) || opts[0];
        },
        onOptionChange: function(fineId, option) {
            if (!option) return;
            this.$set(this.selectedTypes, fineId, option.Type);
            this.updateItems();
        },
        updateItems: function() {
            var self = this;
            var items = self.selectedFines.map(function(fine) {
                var opt = self.getOption(fine.EmployeeTrafficFineId);
                return {
                    EmployeeTrafficFineId: fine.EmployeeTrafficFineId,
                    PaidAmount: opt ? opt.PaymentAmount : 0,
                };
            });
            self.$set(self.value, "Items", items);
        },
        formatCurrency: function(val) {
            if (!val) return "0,00";
            return Number(val).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        getActualEmployee: function(fine) {
            var opt = this.getOption(fine.EmployeeTrafficFineId);
            if (!opt) return null;
            var paid = Number(opt.PaymentAmount);
            var planned = Number(fine.PlannedDriverAmount || 0);
            var total = planned + Number(fine.PlannedCompanyAmount || 0);
            if (!paid || !total) return null;
            return Math.round(paid * planned / total * 100) / 100;
        },
        getActualCompany: function(fine) {
            var opt = this.getOption(fine.EmployeeTrafficFineId);
            if (!opt) return null;
            var paid = Number(opt.PaymentAmount);
            var actual = this.getActualEmployee(fine);
            if (actual === null) return null;
            return Math.round((paid - actual) * 100) / 100;
        },
        getPaymentPlaces: function() {
            SystemDataApi.TrafficFinePaymentPlace().then(function(res) {
                this.paymentPlaces = res.data.Data || [];
            }.bind(this));
        },
        getTaxAdministrators: function() {
            var self = this;
            SystemDataApi.TaxAdministrationList().then(function(res) {
                self.taxAdministrators = res.data.Data || [];
                var defaultTax = self.taxAdministrators.find(function(t) {
                    return t.Name && t.Name.toUpperCase().includes("ÇINAR");
                });
                if (defaultTax) {
                    self.defaultTaxAdministratorId = defaultTax.Id;
                    self.$set(self.value, "TaxAdministrationId", defaultTax.Id);
                }
            });
        },
        getBanks: function() {
            BankApi.List().then(function(res) {
                this.banks = res.data.Data || [];
            }.bind(this));
        },
        calculateAll: function(paymentDate) {
            var self = this;
            var dateStr;
            if (paymentDate instanceof Date) {
                var y = paymentDate.getFullYear();
                var m = String(paymentDate.getMonth() + 1).padStart(2, "0");
                var d = String(paymentDate.getDate()).padStart(2, "0");
                dateStr = y + "-" + m + "-" + d;
            } else {
                dateStr = paymentDate;
            }
            self.calculating = true;
            var results = {};
            var promises = self.selectedFines.map(function(fine) {
                return EmployeeOperationsApi.TrafficFine.Payment.Calculate(fine.EmployeeTrafficFineId, dateStr)
                    .then(function(res) {
                        if (res.data.Success && res.data.Data && res.data.Data.length > 0) {
                            results[fine.EmployeeTrafficFineId] = res.data.Data;
                        }
                    })
                    .catch(function() {});
            });
            Promise.all(promises).then(function() {
                self.fineAmounts = results;
                self.selectedTypes = {};
                self.updateItems();
                self.calculating = false;
            });
        },
        disableFutureDates: function(date) {
            var today = new Date();
            today.setHours(23, 59, 59, 999);
            return date > today;
        },
    },
    computed: {
        paymentPlace: {
            get: function() {
                return this.paymentPlaces.find(function(p) { return p.Value == this.value.PaymentPlace; }.bind(this));
            },
            set: function(val) {
                this.value.PaymentPlace = val ? val.Value : 0;
            },
        },
        taxAdministrator: {
            get: function() {
                return this.taxAdministrators.find(function(t) { return t.Id == this.value.TaxAdministrationId; }.bind(this));
            },
            set: function(val) {
                this.value.TaxAdministrationId = val ? val.Id : null;
            },
        },
        bank: {
            get: function() {
                return this.banks.find(function(b) { return b.Id == this.value.BankId; }.bind(this));
            },
            set: function(val) {
                this.value.BankId = val ? val.Id : null;
            },
        },
    },
};
</script>
